import axios, { AxiosInstance } from "axios";
import type { ApiClientConfig } from "./types";

export type { ApiClientConfig, UserRole } from "./types";

/**
 * API Client 팩토리 함수
 * 역할별 설정을 주입받아 axios 인스턴스를 생성합니다.
 */
export function createApiClient(config: ApiClientConfig): AxiosInstance {
  const {
    localStorageKey,
    loginRedirectPath,
    handle403 = false,
    pendingApprovalPath = "/pending-approval",
    baseURL = "/backend-api",
  } = config;

  const client = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });

  // 응답 인터셉터: 에러 처리
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // 클라이언트 사이드에서만 처리
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;

        // 401 Unauthorized: 인증 실패
        if (error.response?.status === 401) {
          // 로그인 페이지에서 발생한 401은 사용자 친화적인 메시지로 변경
          if (currentPath === loginRedirectPath) {
            const friendlyError = {
              ...error,
              message: "아이디와 비밀번호를 확인해주세요.",
            };
            return Promise.reject(friendlyError);
          }

          // 아이디 찾기, 비밀번호 찾기 등 인증이 필요 없는 페이지에서는 에러만 반환
          if (
            currentPath === "/auth/find-id" ||
            currentPath === "/auth/find-password"
          ) {
            return Promise.reject(error);
          }

          // 다른 페이지에서 401 발생 시 로그인 페이지로 리다이렉트
          localStorage.removeItem(localStorageKey);
          window.location.replace(loginRedirectPath);
        }

        // 403 Forbidden: 승인되지 않은 사용자 (student만 처리)
        if (handle403 && error.response?.status === 403) {
          if (currentPath !== pendingApprovalPath) {
            window.location.replace(pendingApprovalPath);
          }
        }
      }
      return Promise.reject(error);
    }
  );

  // 요청 인터셉터: pending-approval 페이지에서의 API 호출 제한 (student만)
  if (handle403) {
    client.interceptors.request.use(
      async (requestConfig) => {
        // pending-approval 페이지에서 /auth/me 호출하는 경우
        // (승인 상태 확인용이므로 통과)
        if (
          typeof window !== "undefined" &&
          window.location.pathname === pendingApprovalPath &&
          requestConfig.url === "/auth/me"
        ) {
          return requestConfig;
        }

        return requestConfig;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  return client;
}
